import { component as componentBuilder } from '../core/component.js';
import { $ as jquery } from 'jquery';

var $ = jquery;

function toArray(list) {
  return Array.prototype.slice.call(list || []);
}

function matchesSelector(element, selector, root) {
  if (!element || element === root) {
    return false;
  }

  if (!selector || selector === '> *') {
    return element.parentElement === root;
  }

  var current = element;
  while (current && current !== root) {
    if (current.matches && current.matches(selector)) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
}

function closestItem(element, selector, root) {
  var current = element;
  while (current && current !== root) {
    if (matchesSelector(current, selector, root)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function inferDirection(items) {
  if (items.length < 2) {
    return 'vertical';
  }
  var first = items[0].getBoundingClientRect();
  var second = items[1].getBoundingClientRect();
  return Math.abs(first.top - second.top) < 8 ? 'horizontal' : 'vertical';
}

class Sortable {
  constructor(el, options) {
    if (!el) {
      throw new Error('Sortable requires a root element');
    }

    this.el = el;
    this.options = Object.assign(
      {
        draggable: '> *',
        handle: null,
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
      },
      options || {},
    );

    this.dragged = null;
    this.oldIndex = -1;
    this.newIndex = -1;
    this.boundDragStart = this.onDragStart.bind(this);
    this.boundDragOver = this.onDragOver.bind(this);
    this.boundDrop = this.onDrop.bind(this);
    this.boundDragEnd = this.onDragEnd.bind(this);

    this.refresh();
    this.bind();
  }

  getItems() {
    var selector = this.options.draggable;
    return toArray(this.el.children).filter(function (child) {
      return matchesSelector(child, selector, child.parentElement);
    });
  }

  refresh() {
    var animation = this.options.animation;
    var handle = this.options.handle;
    this.getItems().forEach(function (item) {
      item.setAttribute('draggable', handle ? 'false' : 'true');
      if (handle) {
        toArray(item.querySelectorAll(handle)).forEach(function (handleElem) {
          handleElem.setAttribute('draggable', 'true');
        });
      }
      if (animation) {
        item.style.transition = 'transform ' + animation + 'ms ease';
      }
    });
    return this;
  }

  bind() {
    this.el.addEventListener('dragstart', this.boundDragStart);
    this.el.addEventListener('dragover', this.boundDragOver);
    this.el.addEventListener('drop', this.boundDrop);
    this.el.addEventListener('dragend', this.boundDragEnd);
  }

  unbind() {
    this.el.removeEventListener('dragstart', this.boundDragStart);
    this.el.removeEventListener('dragover', this.boundDragOver);
    this.el.removeEventListener('drop', this.boundDrop);
    this.el.removeEventListener('dragend', this.boundDragEnd);
  }

  onDragStart(event) {
    var item = closestItem(event.target, this.options.draggable, this.el);
    if (!item) {
      var handleItem = closestItem(event.target.closest(this.options.handle || '*'), this.options.draggable, this.el);
      item = handleItem;
    }
    if (!item) {
      event.preventDefault();
      return;
    }

    if (this.options.handle) {
      var handle = event.target.closest(this.options.handle);
      if (!handle || !item.contains(handle)) {
        event.preventDefault();
        return;
      }
    }

    this.dragged = item;
    this.oldIndex = this.getItems().indexOf(item);
    this.newIndex = this.oldIndex;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', '');
    }

    item.classList.add(this.options.chosenClass);
    item.classList.add(this.options.dragClass);
    item.classList.add(this.options.ghostClass);

    if (typeof this.options.onStart === 'function') {
      this.options.onStart(this.createEventPayload(event));
    }
  }

  onDragOver(event) {
    if (!this.dragged) {
      return;
    }

    event.preventDefault();

    var items = this.getItems().filter(function (item) {
      return item !== this.dragged;
    }, this);

    if (!items.length) {
      return;
    }

    var target = closestItem(event.target, this.options.draggable, this.el);
    if (!target || target === this.dragged) {
      target = items[items.length - 1];
    }

    var direction =
      typeof this.options.direction === 'function'
        ? this.options.direction(event, target, this.dragged)
        : this.options.direction || inferDirection(items);
    var rect = target.getBoundingClientRect();
    var before =
      direction === 'horizontal'
        ? event.clientX < rect.left + rect.width / 2
        : event.clientY < rect.top + rect.height / 2;

    if (before) {
      if (target !== this.dragged.nextSibling) {
        this.el.insertBefore(this.dragged, target);
      }
    } else if (target.nextSibling !== this.dragged) {
      this.el.insertBefore(this.dragged, target.nextSibling);
    }

    this.newIndex = this.getItems().indexOf(this.dragged);
  }

  onDrop(event) {
    if (!this.dragged) {
      return;
    }
    event.preventDefault();
  }

  onDragEnd(event) {
    if (!this.dragged) {
      return;
    }

    var item = this.dragged;
    item.classList.remove(this.options.chosenClass);
    item.classList.remove(this.options.dragClass);
    item.classList.remove(this.options.ghostClass);

    this.newIndex = this.getItems().indexOf(item);

    if (typeof this.options.onEnd === 'function') {
      this.options.onEnd(this.createEventPayload(event));
    }

    this.dragged = null;
  }

  createEventPayload(originalEvent) {
    return {
      item: this.dragged,
      from: this.el,
      to: this.el,
      oldIndex: this.oldIndex,
      newIndex: this.newIndex,
      originalEvent: originalEvent,
    };
  }

  option(name, value) {
    if (arguments.length === 1) {
      return this.options[name];
    }
    this.options[name] = value;
    if (name === 'animation' || name === 'draggable') {
      this.refresh();
    }
    return this;
  }

  toArray(attribute) {
    var attr = attribute || 'data-id';
    return this.getItems().map(function (item) {
      return item.getAttribute(attr) || '';
    });
  }

  sort(order, attribute) {
    var attr = attribute || 'data-id';
    var items = this.getItems();
    var map = Object.create(null);

    items.forEach(function (item) {
      map[item.getAttribute(attr)] = item;
    });

    order.forEach(
      function (value) {
        if (map[value]) {
          this.el.appendChild(map[value]);
        }
      }.bind(this),
    );

    return this;
  }

  destroy() {
    var items = this.getItems();
    this.unbind();
    items.forEach(
      function (item) {
        item.removeAttribute('draggable');
        item.classList.remove(this.options.chosenClass);
        item.classList.remove(this.options.dragClass);
        item.classList.remove(this.options.ghostClass);
      }.bind(this),
    );
    this.el = null;
  }
}

$.fn.sortable = function (options) {
  var returnValue;
  var args = arguments;

  this.each(function () {
    var element = this;
    var instance = $(element).data('sortable');

    if (!instance && (typeof options === 'object' || options == null)) {
      instance = new Sortable(element, options || {});
      $(element).data('sortable', instance);
      return;
    }

    if (!instance) {
      return;
    }

    if (options === 'destroy') {
      instance.destroy();
      $(element).removeData('sortable');
      return;
    }

    if (options === 'widget') {
      returnValue = instance;
      return;
    }

    if (typeof instance[options] === 'function') {
      returnValue = instance[options].apply(
        instance,
        Array.prototype.slice.call(args, 1),
      );
      return;
    }

    if (typeof options === 'string') {
      if (args.length === 1) {
        returnValue = instance.option(options);
      } else {
        instance.option(options, args[1]);
      }
    }
  });

  return returnValue === undefined ? this : returnValue;
};

const sortable = componentBuilder({
  name: 'sortable',
  config: {
    elem: null,
    draggable: '> *',
    handle: null,
    animation: 150,
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    onStart: null,
    onEnd: null,
  },
  render: function () {
    var that = this;
    var options = that.config;
    var elem = options.elem[0];
    var sortableOptions = $.extend({}, options);

    delete sortableOptions.elem;
    delete sortableOptions.id;

    if (that.sortable) {
      that.sortable.destroy();
    }

    that.sortable = new Sortable(elem, sortableOptions);
  },
  extendsInstance: function () {
    var that = this;
    return {
      getSortable: function () {
        return that.sortable;
      },
      refresh: function () {
        that.sortable && that.sortable.refresh();
        return this;
      },
      destroy: function () {
        if (that.sortable) {
          that.sortable.destroy();
          that.sortable = null;
        }
        sortable.removeInst(this.id);
      },
      toArray: function (attribute) {
        return that.sortable ? that.sortable.toArray(attribute) : [];
      },
      sort: function (order, attribute) {
        that.sortable && that.sortable.sort(order, attribute);
        return this;
      },
    };
  },
});

sortable.Sortable = Sortable;
sortable.jquery = $.fn.sortable;

if (typeof window !== 'undefined') {
  window.Sortable = Sortable;
  if (window.jQuery) {
    window.jQuery.fn.sortable = $.fn.sortable;
  }
}

export { Sortable, sortable };
